import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../config";
import {
    FiShield,
    FiAlertTriangle,
    FiUsers,
    FiActivity,
    FiRefreshCw,
    FiTrash2,
    FiGlobe,
    FiCheckCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
    Button,
    Card,
    PageHeader,
    StatCard,
    Badge,
    DataTable,
    EmptyState,
    Tooltip,
    useConfirm,
    formatTime,
} from "./ui";

export default function SecurityPanel() {
    const confirm = useConfirm();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/security`);
            const data = await res.json();
            if (data.success) {
                setStats(data);
                setLastUpdated(new Date());
            }
        } catch (err) {
            console.error("Error fetching security stats:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
        let interval;
        if (autoRefresh) {
            interval = setInterval(fetchStats, 10000);
        }
        return () => clearInterval(interval);
    }, [fetchStats, autoRefresh]);

    const handleReset = async () => {
        const ok = await confirm({
            title: "Reset Statistik Keamanan",
            message: "Reset semua statistik keamanan? Tindakan ini tidak dapat dibatalkan.",
            confirmLabel: "Reset",
            tone: "danger",
        });
        if (!ok) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/admin/security/reset`, {
                method: "POST",
            });
            const data = await res.json();
            if (data.success) {
                toast.success("Statistik keamanan direset");
                fetchStats();
            }
        } catch (err) {
            toast.error("Gagal reset: " + err.message);
        }
    };

    const formatUptime = (seconds) => {
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        if (d > 0) return `${d}h ${h}j ${m}m`;
        if (h > 0) return `${h}j ${m}m`;
        return `${m}m`;
    };

    const eventTone = (type) => (type === "rate_limited" ? "danger" : type === "suspicious" ? "warning" : "slate");

    const blockedColumns = [
        { key: "ip", header: "IP Address", cell: (ip) => <span className="font-mono text-sm text-slate-800 dark:text-slate-200">{ip.ip}</span> },
        { key: "blocked_count", header: "Blocked", headerClassName: "text-center", className: "text-center", cell: (ip) => <Badge tone="danger">{ip.blocked_count}</Badge> },
        { key: "total_requests", header: "Total", headerClassName: "text-center", className: "text-center", cell: (ip) => <span className="text-sm text-slate-600 dark:text-slate-400">{ip.total_requests}</span> },
        { key: "last_seen", header: "Terakhir", headerClassName: "text-right", className: "text-right", cell: (ip) => <span className="text-xs text-slate-500 dark:text-slate-400">{formatTime(ip.last_seen)}</span> },
    ];

    const eventColumns = [
        { key: "timestamp", header: "Waktu", cell: (e) => <span className="text-xs text-slate-500 dark:text-slate-400">{formatTime(e.timestamp)}</span> },
        { key: "ip", header: "IP", cell: (e) => <span className="font-mono text-sm text-slate-800 dark:text-slate-200">{e.ip}</span> },
        { key: "path", header: "Path", hideBelow: "lg", cell: (e) => <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{e.path}</span> },
        { key: "type", header: "Tipe", cell: (e) => <Badge tone={eventTone(e.type)}>{e.type === "rate_limited" ? "Rate Limited" : e.type}</Badge> },
    ];

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-9 w-40 rounded-lg bg-slate-200 dark:bg-slate-800" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
                    ))}
                </div>
            </div>
        );
    }

    const secure = (stats?.total_blocked || 0) === 0;

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader
                title="Keamanan"
                subtitle="Monitor keamanan server dan rate limiting"
                icon={FiShield}
                actions={
                    <>
                        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3.5 py-2 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500"
                            />
                            Auto-refresh
                        </label>
                        <Tooltip label="Refresh sekarang">
                            <Button variant="secondary" size="icon" onClick={fetchStats} aria-label="Refresh">
                                <FiRefreshCw className="h-4 w-4" />
                            </Button>
                        </Tooltip>
                        <Tooltip label="Reset statistik">
                            <Button variant="secondary" size="icon" onClick={handleReset} className="text-danger-600 dark:text-danger-400" aria-label="Reset statistik">
                                <FiTrash2 className="h-4 w-4" />
                            </Button>
                        </Tooltip>
                    </>
                }
            />

            {/* Status banner */}
            <Card className={`p-4 ${secure ? "border-success-100 bg-success-50/60 dark:border-success-500/20 dark:bg-success-500/10" : "border-warning-100 bg-warning-50/60 dark:border-warning-500/20 dark:bg-warning-500/10"}`}>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${secure ? "bg-success-100 text-success-600 dark:bg-success-500/20 dark:text-success-400" : "bg-warning-100 text-warning-600 dark:bg-warning-500/20 dark:text-warning-400"}`}>
                            {secure ? <FiShield className="h-5 w-5" /> : <FiAlertTriangle className="h-5 w-5" />}
                        </span>
                        <div>
                            <p className={`text-sm font-semibold ${secure ? "text-success-700 dark:text-success-300" : "text-warning-700 dark:text-warning-300"}`}>
                                {secure ? "Server Secure" : "Perlu Perhatian"}
                            </p>
                            <p className={`text-xs ${secure ? "text-success-600 dark:text-success-400" : "text-warning-600 dark:text-warning-400"}`}>
                                {autoRefresh ? "Monitoring Active" : "Auto-refresh nonaktif"}
                            </p>
                        </div>
                    </div>
                    {lastUpdated && (
                        <span className="hidden text-xs text-slate-500 dark:text-slate-400 sm:block">
                            Terakhir diperbarui: {lastUpdated.toLocaleTimeString("id-ID")}
                        </span>
                    )}
                </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Total Request" value={(stats?.total_requests || 0).toLocaleString("id-ID")} icon={FiActivity} tone="primary" hint={`Uptime: ${formatUptime(stats?.uptime_seconds || 0)}`} />
                <StatCard label="Terblokir" value={(stats?.total_blocked || 0).toLocaleString("id-ID")} icon={FiShield} tone="danger" hint="Request ditolak (rate limited)" />
                <StatCard label="IP Unik" value={stats?.unique_ips || 0} icon={FiGlobe} tone="secondary" hint={`${stats?.blocked_ips || 0} IP pernah diblokir`} />
                <StatCard label="Rate Limit" value="60/mnt" icon={FiAlertTriangle} tone="warning" hint="Sensitif: 3-5/min" />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Top blocked IPs */}
                <Card>
                    <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                        <FiUsers className="h-5 w-5 text-danger-500" />
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Top IP Terblokir</h3>
                    </div>
                    <DataTable
                        columns={blockedColumns}
                        rows={(stats?.top_blocked_ips || []).slice(0, 10)}
                        rowKey={(ip, i) => `${ip.ip}-${i}`}
                        empty={
                            <EmptyState
                                icon={FiShield}
                                title="Tidak ada IP terblokir"
                                description="Tidak ada aktivitas mencurigakan."
                                className="py-10"
                            />
                        }
                    />
                </Card>

                {/* Top attacked paths */}
                <Card>
                    <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                        <FiAlertTriangle className="h-5 w-5 text-warning-500" />
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Path Paling Sering Diserang</h3>
                    </div>
                    <div className="space-y-3 p-5">
                        {(stats?.top_attacked_paths || []).length === 0 ? (
                            <EmptyState icon={FiCheckCircle} title="Tidak ada serangan terdeteksi" description="Sistem terlihat aman." className="py-8" />
                        ) : (
                            (stats?.top_attacked_paths || []).map((path, i) => {
                                const maxCount = stats?.top_attacked_paths?.[0]?.count || 1;
                                const percentage = (path.count / maxCount) * 100;
                                return (
                                    <div key={i}>
                                        <div className="mb-1 flex items-center justify-between gap-3">
                                            <span className="truncate font-mono text-xs text-slate-600 dark:text-slate-300" title={path.path}>
                                                {path.path}
                                            </span>
                                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{path.count}</span>
                                        </div>
                                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-warning-400 to-danger-500 transition-all"
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </Card>
            </div>

            {/* Recent events */}
            <Card>
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <FiActivity className="h-5 w-5 text-primary-500" />
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Event Keamanan Terbaru</h3>
                    </div>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{(stats?.recent_events || []).length} event</span>
                </div>
                <DataTable
                    columns={eventColumns}
                    rows={(stats?.recent_events || []).slice(0, 30)}
                    rowKey={(e, i) => `${e.timestamp}-${e.ip}-${i}`}
                    empty={<EmptyState icon={FiActivity} title="Belum ada event keamanan" className="py-10" />}
                />
            </Card>
        </div>
    );
}
